import isNull from 'lodash/isNull';
import { Duration } from 'luxon';
import moment from 'moment';
import {
  AgeRangeValue,
  BasicSelectedValue,
  MedAdminTimeValue,
  RequiredFilterValues,
} from '../store/reporting/reportingTypes';
import { SoftwareVersion } from '../store/software/softwareTypes';
import {
  ONE_YEAR_IN_MSECONDS,
  UserRoles,
  SOC_TYPE,
  MED_EVENT_STATUS_ID,
} from './constants';
import {
  format,
  formatDistanceToNow,
  isWithinInterval,
  subHours,
} from 'date-fns';
import { lightGrey } from '../mainTheme';

// URL/API Functions

export const formatUrl = (host: string, path: string = '') => {
  return 'https://' + host + '/' + path;
};

export const jsonToFormUrlEncoded = (json: { [key: string]: any }) => {
  let params = new URLSearchParams(json);
  return params.toString();
};

// Host Functions

interface Config {
  rest_host: string;
  cookie_domain: string;
}

let config: Config = {
  rest_host: '',
  cookie_domain: '',
};

export const setRestHost = (rest_host: string) => {
  config.rest_host = rest_host;
};
export const getRestHost = () => {
  return config.rest_host;
};
export const setCookieDomain = (cookie_domain: string) => {
  config.cookie_domain = cookie_domain;
};
export const getCookieDomain = () => {
  return config.cookie_domain;
};

// Cookie Functions

export const setCookie = (
  cname: string,
  cvalue: string,
  expires_in?: number,
) => {
  var d = new Date();
  if (expires_in) {
    d.setTime(d.getTime() + expires_in * 1000);
  } else {
    d.setTime(d.getTime() + ONE_YEAR_IN_MSECONDS);
  }
  var expires = ' expires=' + d.toUTCString();
  // Host-scoped (no `domain=`): these are auth tokens only the WebAdmin reads
  // (REST takes them via Authorization: Bearer). A broad `.internal.omcare.com`
  // scope leaked ~4 KB of cookies onto the Janus/socket WS-upgrade handshake,
  // overflowing libwebsockets' header buffer and breaking calls (OI-1564).
  document.cookie = cname + '=' + cvalue + ';' + expires + ';path=/';
};

export const getCookie = (cookie_name: string) => {
  var cookies = document.cookie.split(/[\s,;]+/);
  for (var i = 0; i < cookies.length; i++) {
    var c = cookies[i].split('=');
    if (c[0] === cookie_name) {
      return c[1];
    }
  }
};

// Shape of the claims we read out of the JWT access token. The token mirrors
// the auth/validate response, which nests the signed-in profile under `user`.
export interface DecodedAccessToken {
  exp?: number;
  user?: {
    pharmacy_name?: string | null;
    pharmacy_id?: number;
    [key: string]: unknown;
  };
  pharmacy_name?: string | null;
  [key: string]: unknown;
}

// Decode (without verifying) the JWT payload. Falls back to the access_token
// cookie when no token is passed. Returns null if anything is malformed.
export const decodeAccessToken = (
  token?: string | null,
): DecodedAccessToken | null => {
  const accessToken = token ?? getCookie('access_token');
  if (!accessToken) return null;
  try {
    const base64Url = accessToken.split('.')[1];
    if (!base64Url) return null;
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const jsonPayload = decodeURIComponent(
      window
        .atob(base64)
        .split('')
        .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
        .join(''),
    );
    return JSON.parse(jsonPayload) as DecodedAccessToken;
  } catch (e) {
    console.error('Failed to decode access token', e);
    return null;
  }
};

// Pull the pharmacy name out of the access token (checks both the nested
// `user` claim and a top-level claim).
export const getPharmacyNameFromToken = (
  token?: string | null,
): string | null => {
  const payload = decodeAccessToken(token);
  return payload?.user?.pharmacy_name ?? payload?.pharmacy_name ?? null;
};

export const deleteCookie = (name: string) => {
  let epoch = new Date(0);
  let epoch_string = epoch.toUTCString();
  // Clear the host-scoped cookie and, for migration, any legacy broad-domain one.
  document.cookie = `${name}=; path=/; expires=${epoch_string}`;
  let domain = getCookieDomain();
  if (domain) {
    document.cookie = `${name}=; domain=${domain}; path=/; expires=${epoch_string}`;
  }
};

export const deleteToken = () => {
  deleteCookie('access_token');
  deleteCookie('id_token');
  deleteCookie('refresh_token');
  deleteCookie('token_type');
  redirectToLogin();
};

export const redirectToLogin = () => {
  window.location.href = '/auth/login';
};

// Misc Functions

export const getPrivilegeById = (privilege_id: number): UserRoles => {
  switch (privilege_id) {
    case 1:
      return UserRoles.STANDARD;
    case 2:
      return UserRoles.CUSTOMER_CARE;
    case 3:
      return UserRoles.ENGINEERING;
    case 4:
      return UserRoles.PHARMACY_TECH;
    default:
      return UserRoles.NONE;
  }
};

export const doesVersionExist = (
  software_versions: SoftwareVersion[] | null,
  major: number | null | undefined,
  minor: number | null | undefined,
  prerelease: number | null | undefined,
) => {
  if (!isNull(software_versions)) {
    return (
      software_versions.filter(
        (version: SoftwareVersion) =>
          version.version_major === major &&
          version.version_minor === minor &&
          version.version_prerelease === prerelease &&
          version.soc_id === SOC_TYPE.IMX6,
      ).length !== 0
    );
  }
};

export const getCareTeamRoleById = (careTeam_role_id: number) => {
  switch (careTeam_role_id) {
    case 1:
      return 'Professional caregiver';
    case 2:
      return 'Family administrator';
    case 3:
      return 'Caregiver';
    case 4:
      return 'Friends & family';
    case 5:
      return 'Facility scheduler';
    default:
      return 'unknown';
  }
};

// Standalone palette for the legacy consumers (HubOwnerProfile, CaregiversNew),
// which use borderColor for avatar/text contrast. roles.ts owns the care team
// tab chips; these screens migrate to it in a follow-up.
export const getCareTeamRoleColorById = (careTeam_role_id: number) => {
  switch (careTeam_role_id) {
    case 1: // Professional caregiver
      return {
        backgroundColor: 'var(--Background-B2---Container, #F6F6F9)',
        borderColor: lightGrey,
      };
    case 2: // Family administrator
      return {
        backgroundColor: 'var(--Semantics-S8---Alert-Bg, #FFF4EB)',
        borderColor: '#D18E6C',
      };
    case 3: // Caregiver
      return {
        backgroundColor: 'var(--Outline-02---P---Field-Selected, #D5C7DC)',
        borderColor: '#865E9C',
      };
    case 4: // Friends & family
      return {
        backgroundColor: 'var(--Friends-Family---Bg, #E6F7FF)',
        borderColor: '#B2E0F4',
      };
    case 5: // Facility scheduler
      return {
        backgroundColor: 'var(--Scheduler---Bg, #F0E6FF)',
        borderColor: '#865E9C',
      };
    default: // Default
      return {
        backgroundColor: 'var(--Default---Bg, #F3F3F3)',
        borderColor: '#D9D9D9',
      };
  }
};

export const getFacilityRoleById = (faclity_role_id: number): UserRoles => {
  switch (faclity_role_id) {
    case 1:
      return UserRoles.FACILITY_ADMIN;
    case 2:
      return UserRoles.FACILITY_SCHEDULER;
    case 3:
      return UserRoles.FACILITY_CAREGIVER;
    case 4:
      return UserRoles.FACILITY_OWNER;
    case 5:
      return UserRoles.FACILITY_NURSE;
    default:
      return UserRoles.FACILITY_UNKNOWN;
  }
};

/**
 * Effective role of the signed-in dashboard user. Facility users carry a
 * facility_role_id and resolve by facility role; internal users (no facility)
 * resolve by privilege. Single source for the role gates in components.
 */
export const getCurrentUserRole = (
  user:
    | { facility_role_id?: number | null; privilege_id?: number | null }
    | null
    | undefined,
): UserRoles =>
  user?.facility_role_id
    ? getFacilityRoleById(user.facility_role_id)
    : getPrivilegeById(user?.privilege_id ?? 0);

export const getSoftwareChannelById = (channel_id: number) => {
  switch (channel_id) {
    case 1:
      return 'stable';
    case 2:
      return 'beta';
    default:
      return 'unknown';
  }
};

export const formatPhoneNumber = (phoneNumber: string) => {
  let cleaned = ('' + phoneNumber).replace(/\D/g, '');
  let match = cleaned.match(/^(\d{3})(\d{3})(\d{4})$/);
  if (match) {
    return '(' + match[1] + ') ' + match[2] + '-' + match[3];
  }
  return null;
};

// Filter Utils

export const hasWhiteSpace = (s: string) => {
  return /\s/g.test(s);
};

export const dropDownListFilter = (
  List: BasicSelectedValue[] | string[],
  listSelection: string | MedAdminTimeValue | undefined,
  selectedFilter: BasicSelectedValue,
  buildFilter: (
    selectedFilter: BasicSelectedValue,
    selectedFilterValue: BasicSelectedValue | BasicSelectedValue[],
  ) => void,
) => {
  if (selectedFilter.value === 'state' && listSelection) {
    buildFilter(selectedFilter, {
      display: listSelection as string,
      value: listSelection,
    });
  } else {
    let selectedFilterValue = (List as BasicSelectedValue[]).filter(
      (item) => item.value === listSelection,
    )[0];
    buildFilter(selectedFilter, selectedFilterValue);
  }
};

export const generateBirthDateFromAgeRange = (ageRange: string) => {
  const [toAge, fromAge] = ageRange.split('-');
  const fromBirthDate = moment().subtract(parseInt(fromAge), 'years');
  const toBirthDate = moment().subtract(parseInt(toAge), 'years');
  return {
    fromAge: fromBirthDate.format('MM/DD/yyyy'),
    toAge: toBirthDate.format('MM/DD/yyyy'),
  };
};

export const ageRangeFilter = (
  List: BasicSelectedValue[],
  listSelection: string | AgeRangeValue | undefined,
  ageRange: number[] | AgeRangeValue,
  selectedFilter: BasicSelectedValue,
  buildFilter: (
    selectedFilter: BasicSelectedValue,
    selectedFilterValue: BasicSelectedValue | BasicSelectedValue[],
  ) => void,
) => {
  if (listSelection === 'custom' && (ageRange as number[]).length) {
    const [fromAge, toAge] = ageRange as number[];
    buildFilter(selectedFilter, {
      display: `${fromAge}-${toAge}`,
      value: generateBirthDateFromAgeRange(`${fromAge}-${toAge}`),
    });
  } else {
    let selectedFilterValue = List.filter(
      (item) => item.value === listSelection,
    )[0];
    buildFilter(selectedFilter, selectedFilterValue);
  }
};

export const dateRangeFilter = (
  requiredFilterValues: RequiredFilterValues,
  selectedFilter: BasicSelectedValue,
  buildFilter: (
    selectedFilter: BasicSelectedValue,
    selectedFilterValue: BasicSelectedValue | BasicSelectedValue[],
  ) => void,
) => {
  var selectedFilterValue: BasicSelectedValue | null = null;

  if (
    requiredFilterValues.fromDate === '' ||
    requiredFilterValues.toDate === ''
  ) {
    return;
  }

  if (requiredFilterValues.dateRange === 'datePicker') {
    selectedFilterValue = {
      display: `${requiredFilterValues.fromDate.format(
        'MM/DD/YYYY',
      )} - ${requiredFilterValues.toDate.format('MM/DD/YYYY')}`,
      value: {
        fromDate: requiredFilterValues.fromDate,
        toDate: requiredFilterValues.toDate,
      },
    };
  }

  if (requiredFilterValues.dateRange === 'yesterday') {
    selectedFilterValue = {
      display: requiredFilterValues.fromDate.format('MM/DD/YYYY'),
      value: {
        fromDate: requiredFilterValues.fromDate,
        toDate: requiredFilterValues.toDate,
      },
    };
  }
  if (requiredFilterValues.dateRange === 'last7Days') {
    selectedFilterValue = {
      display: `${requiredFilterValues.fromDate.format(
        'MM/DD/YYYY',
      )} - ${requiredFilterValues.toDate.format('MM/DD/YYYY')}`,
      value: {
        fromDate: requiredFilterValues.fromDate,
        toDate: requiredFilterValues.toDate,
      },
    };
  }
  if (requiredFilterValues.dateRange === 'last14Days') {
    selectedFilterValue = {
      display: `${requiredFilterValues.fromDate.format(
        'MM/DD/YYYY',
      )} - ${requiredFilterValues.toDate.format('MM/DD/YYYY')}`,
      value: {
        fromDate: requiredFilterValues.fromDate,
        toDate: requiredFilterValues.toDate,
      },
    };
  }
  if (requiredFilterValues.dateRange === 'last30Days') {
    selectedFilterValue = {
      display: `${requiredFilterValues.fromDate.format(
        'MM/DD/YYYY',
      )} - ${requiredFilterValues.toDate.format('MM/DD/YYYY')}`,
      value: {
        fromDate: requiredFilterValues.fromDate,
        toDate: requiredFilterValues.toDate,
      },
    };
  }
  if (selectedFilterValue) {
    buildFilter(selectedFilter, selectedFilterValue);
  }
};

export const validZipCode = (zipCode: string) => {
  var isValid = false;
  let regex = new RegExp('^[0-9]{5}?$');
  let regexZipcode = zipCode.match(regex);
  if (regexZipcode !== null && regexZipcode[0] === zipCode) {
    isValid = true;
  }

  return isValid;
};

export const splitZipCodes = (zipCode: string) => {
  let zipCodes = zipCode.split('\n');
  let zipCodeList: string[] = [];
  let invalidZipCodeList: string[] = [];
  if (zipCodes.length > 0) {
    zipCodes.forEach((element) => {
      if (element.trim() !== '') {
        if (element.indexOf(',') > -1) {
          element.split(',').forEach((item) => {
            if (item.trim() !== '') {
              if (validZipCode(item.trim())) {
                if (zipCodeList.indexOf(item.trim()) === -1) {
                  zipCodeList.push(item.trim());
                }
              } else {
                invalidZipCodeList.push(item.trim());
              }
            }
          });
        } else if (hasWhiteSpace(element)) {
          element.split(' ').forEach((item) => {
            if (item.trim() !== '') {
              if (validZipCode(item.trim())) {
                if (zipCodeList.indexOf(item.trim()) === -1) {
                  zipCodeList.push(item.trim());
                }
              } else {
                invalidZipCodeList.push(item.trim());
              }
            }
          });
        } else {
          if (validZipCode(element.trim())) {
            if (zipCodeList.indexOf(element.trim()) === -1) {
              zipCodeList.push(element.trim());
            }
          } else {
            invalidZipCodeList.push(element.trim());
          }
        }
      }
    });
  }
  return { zipCodeList: zipCodeList, invalidZipCodeList: invalidZipCodeList };
};
export const formatCityStateZip = (city: string, ...args: string[]) => {
  const _secondPart = args.filter((item) => item).join(' ');
  return city
    ? _secondPart.length
      ? city + ', ' + _secondPart
      : city
    : _secondPart;
};

export const convertMinutesToSeconds = (minutes: number) => {
  return minutes * 60;
};

export const convertSecondsToMinutes = (seconds: number) => {
  return seconds / 60;
};

export const convertHoursToSeconds = (hours: string): number => {
  const numericHours = parseInt(hours.split(' ')[0], 10);
  return numericHours * 60 * 60;
};

// Function to convert minutes into a human-readable format using Luxon
export const formatDurationFromMinutes = (minutes: number) => {
  const duration = Duration.fromObject({ minutes });

  // Check if the duration is less than an hour
  if (minutes < 60) {
    return duration.toFormat("m 'minute(s)'");
  }
  // For durations of one hour or more, format as "h 'hour(s)' m 'minute(s)'"
  return duration
    .toFormat("h 'hour(s)' m 'minute(s)'")
    .replace(' 0 minute(s)', '');
};

// User roles for new UI implementation. Drives isRoleEligibleForNewUI, which
// controls whether the Classic/Modern toggle is shown. PHARMACY_TECH is
// intentionally excluded: their view is modern-design only, so they must never
// see the toggle (there is no working classic UI for them to fall back to).
export const USER_ROLES_FOR_NEW_UI = [
  UserRoles.FACILITY_ADMIN, // Commenting out as admin role prefers the classic UI by default
  UserRoles.CUSTOMER_CARE,
  UserRoles.ENGINEERING,
];

// Function to check if a role is eligible for the new UI
export const isRoleEligibleForNewUI = (role: UserRoles) => {
  return USER_ROLES_FOR_NEW_UI.includes(role);
};

// Function to get the default UI mode for a role
// Returns true for Modern UI, false for Classic UI
export const getDefaultUIModeForRole = (role: UserRoles): boolean => {
  switch (role) {
    case UserRoles.FACILITY_ADMIN:
      return true; // Modern UI
    case UserRoles.CUSTOMER_CARE:
      return true;
    case UserRoles.ENGINEERING:
      return true;
    case UserRoles.PHARMACY_TECH:
      return true; // Pharmacy tech view is modern-design only
    default:
      return false; // Classic UI for all other roles
  }
};

export enum MedStatusEnum {
  UpToDate = 'up to date',
  Missed = 'Missed',
}

// Function for Date wihout coverting to Local timezone
export const formatDate = (dateString: string) => {
  const date = moment.parseZone(dateString); // Parse the date with its time zone eg format: 2024-11-18T19:00:00.000-06:00
  return date.isValid()
    ? date.format('ddd MMM DD YYYY, h:mm a')
    : 'Invalid date';
};

export const parseDateToLocal = (
  utcDateString: string,
  formatString: string,
): string => {
  // Append 'Z' to the string to indicate it's in UTC if it doesn't contain 'T' or timezone information
  if (!utcDateString.includes('T')) {
    utcDateString += 'Z';
  }
  const date = new Date(utcDateString);
  // Format the date to the desired output
  return format(date, formatString);
};

// Format the date portion of an API timestamp as-is, with no timezone conversion.
// Reads YYYY-MM-DD from the start of the string and formats those calendar values directly.
export const formatDateIgnoreTz = (
  dateString: string | null | undefined,
  formatString: string,
): string => {
  if (!dateString) return 'N/A';
  const [year, month, day] = dateString.slice(0, 10).split('-').map(Number);
  if (!year || !month || !day) return 'N/A';
  return format(new Date(year, month - 1, day), formatString);
};

export const getJSONOrString = (message: string) => {
  try {
    return JSON.parse(message);
  } catch (err) {
    return message;
  }
};

export const capitalizeFirstLetter = (value?: string | null) => {
  if (!value) return 'N/A';

  // Replace underscores with spaces
  const formatted = value.replace(/_/g, ' ').toLowerCase();

  // Capitalize only the first letter of the entire string
  return formatted.charAt(0).toUpperCase() + formatted.slice(1);
};

export const formatEventTime = (timestamp: string | undefined) => {
  if (!timestamp) return 'N/A';

  const date = new Date(timestamp);
  const now = new Date();
  const is24HoursOld = isWithinInterval(date, {
    start: subHours(now, 24),
    end: now,
  });

  if (is24HoursOld) {
    return formatDistanceToNow(date, { addSuffix: true });
  }

  return parseDateToLocal(timestamp, 'yyyy-MM-dd HH:mm:ss');
};

// Drug usage status enum for medication
export const DRUG_USAGE_STATUS = {
  ACTIVE: 1,
  INACTIVE: 2,
  DISCONTINUED: 3,
} as const;

// Pulls skipped_reasons off a med event when its status is Skipped.
// Localizes the cast around the codegen type quirk where the generated key
// is literally `'skipped_reasons?'`.
export const getSkippedReasons = (event: {
  status_id: number;
  event_data?: { skipped_reasons?: string[] } | Record<string, unknown>;
}): string[] => {
  if (event.status_id !== MED_EVENT_STATUS_ID.Skipped) return [];
  const data = event.event_data as { skipped_reasons?: string[] } | undefined;
  return data?.skipped_reasons ?? [];
};

// Shared phone-format validator for the (123) 456-7890 mask used across forms.
export const PHONE_REGEX = /^\(\d{3}\) \d{3}-\d{4}$/;

// this function formats a phone number to (123) 456-7890 format as B.E do .
export function formatPhone(phone: string | null | undefined): string {
  if (!phone) return '';
  // Remove all non-digit characters
  const digits = phone.replace(/\D/g, '');
  // If not exactly 10 digits, return as is (avoid breaking other formats)
  if (digits.length !== 10) return phone;
  // Format as (123) 456-7890
  return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
}

// Message to show for a failed generated-client call (ApiError) or axios
// error: the API's `{ message }`, else a short plain-text body, else the
// status text of a 4xx ("Forbidden", "Not Found"), else `fallback`. A long or
// HTML body (a proxy 502 page) is not something to put in a toast or a panel,
// and neither a 5xx's status text ("Internal Server Error") nor a transport
// error's own text ("Network Error") beats the caller's specific fallback.
const MAX_API_ERROR_BODY_LENGTH = 200;

export const getApiErrorMessage = (err: any, fallback: string): string => {
  const bodyMessage = err?.body?.message ?? err?.response?.data?.message;
  if (typeof bodyMessage === 'string' && bodyMessage) return bodyMessage;
  const body = err?.body ?? err?.response?.data;
  if (
    typeof body === 'string' &&
    body &&
    body.length <= MAX_API_ERROR_BODY_LENGTH &&
    !body.trimStart().startsWith('<')
  ) {
    return body;
  }
  // ApiError (generated client) and axios errors carry the HTTP status. Only
  // a client error's status text says something the reader can act on.
  const status = err?.status ?? err?.response?.status;
  const isClientError =
    typeof status === 'number' && status >= 400 && status < 500;
  if (isClientError && typeof err?.message === 'string' && err.message) {
    return err.message;
  }
  return fallback;
};

// IANA zone of the viewer's browser, sent as `time_zone` to the survey
// insights and survey history endpoints so the server cuts the rolling window
// on the same days the client shows times in. Etc/UTC when the runtime
// cannot say.
export const getBrowserTimeZone = (): string => {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || 'Etc/UTC';
  } catch {
    return 'Etc/UTC';
  }
};
